import pathParse from "path-parse";
import { zippableSoftwareExtentions, zippableSoftwareFolders } from "../../content/workFileFormatsGroups";

export const FOLDER_MANIFEST_VERSION = 1;

export const folderUploadClassifications = {
  DAW_FOLDER: "DAW_FOLDER",
  ZIPPED_FOLDER: "ZIPPED_FOLDER"
};

export const FOLDER_PACKAGE_UPLOAD_KIND = "folder-package";

function getDroppedFilePath(file) {
  return file.path || file.webkitRelativePath || file.name || "";
}

function getPathSegments(filePath) {
  return filePath.split("/").filter(segment => segment.length !== 0);
}

function buildRelativePath(pathSegments) {
  return pathSegments.slice(1).join("/");
}

function getFolderMarkerExtension(folderName) {
  const folderExtension = (folderName.match(/(\.)\w+/g) || []).pop();
  if (!folderExtension) return "";
  return zippableSoftwareFolders.find(ext => ext === folderExtension.toLowerCase()) || "";
}

function getDawFileMarker(filePath) {
  const parsedPath = pathParse(filePath);
  const fileExtension = parsedPath.ext ? parsedPath.ext.toLowerCase() : "";
  if (!fileExtension) return null;

  const markerExtension = zippableSoftwareExtentions.find(ext => ext === fileExtension);
  if (!markerExtension) return null;

  return {
    type: "file",
    extension: markerExtension,
    relativePath: filePath
  };
}

export function identifyDroppedFolderRoots(files) {
  const folderRoots = [];
  files.forEach(file => {
    const pathSegments = getPathSegments(getDroppedFilePath(file));
    if (pathSegments.length < 2) return;

    const folderRoot = pathSegments[0];
    if (!folderRoots.includes(folderRoot)) {
      folderRoots.push(folderRoot);
    }
  });
  return folderRoots;
}

export function detectDawMarkerForFolder(folderDescriptor) {
  const folderMarkerExtension = getFolderMarkerExtension(folderDescriptor.folderName);
  if (folderMarkerExtension) {
    return {
      type: "folder",
      extension: folderMarkerExtension,
      relativePath: folderDescriptor.folderName
    };
  }

  for (const file of folderDescriptor.files) {
    const fileMarker = getDawFileMarker(file.relativePath);
    if (fileMarker) return fileMarker;
  }

  return null;
}

export function classifyFolderDescriptor(folderDescriptor) {
  return detectDawMarkerForFolder(folderDescriptor)
    ? folderUploadClassifications.DAW_FOLDER
    : folderUploadClassifications.ZIPPED_FOLDER;
}

export function createFolderManifestDescriptors(files) {
  const folderDescriptors = {};

  files.forEach(file => {
    const pathSegments = getPathSegments(getDroppedFilePath(file));
    if (pathSegments.length < 2) return;

    const folderName = pathSegments[0];
    const relativePath = buildRelativePath(pathSegments);
    if (!folderDescriptors[folderName]) {
      folderDescriptors[folderName] = {
        version: FOLDER_MANIFEST_VERSION,
        folderName,
        classification: "",
        dawMarker: null,
        itemCount: 0,
        totalBytes: 0,
        files: []
      };
    }

    const fileSize = Number(file.size) || 0;
    folderDescriptors[folderName].itemCount += 1;
    folderDescriptors[folderName].totalBytes += fileSize;
    folderDescriptors[folderName].files.push({
      name: file.name || pathSegments[pathSegments.length - 1],
      relativePath,
      size: fileSize,
      type: file.type || "",
      lastModified: file.lastModified || null
    });
  });

  return Object.values(folderDescriptors).map(folderDescriptor => {
    const dawMarker = detectDawMarkerForFolder(folderDescriptor);
    return {
      ...folderDescriptor,
      classification: dawMarker
        ? folderUploadClassifications.DAW_FOLDER
        : folderUploadClassifications.ZIPPED_FOLDER,
      dawMarker
    };
  });
}

export function createFolderPackageOutputs(acceptedFiles) {
  const folderDescriptors = createFolderManifestDescriptors(acceptedFiles);
  if (!folderDescriptors.length) return [];

  return folderDescriptors.map(manifest => {
    const rawFiles = acceptedFiles.reduce((arr, file) => {
      const pathSegments = getPathSegments(getDroppedFilePath(file));
      if (pathSegments.length < 2 || pathSegments[0] !== manifest.folderName) {
        return arr;
      }

      return arr.concat({
        file,
        name: file.name || pathSegments[pathSegments.length - 1],
        relativePath: buildRelativePath(pathSegments),
        size: Number(file.size) || 0,
        type: file.type || "",
        lastModified: file.lastModified || null
      });
    }, []);

    return {
      kind: FOLDER_PACKAGE_UPLOAD_KIND,
      folderName: manifest.folderName,
      manifest,
      rawFiles,
      ext: manifest.dawMarker ? manifest.dawMarker.extension : ".zip"
    };
  });
}

export async function prepareUploadedFilesForServerPackaging(acceptedFiles) {
  return prepareUploadedFiles(acceptedFiles);
}

export async function prepareUploadedFiles(acceptedFiles) {
  if (!acceptedFiles.length) return [];
  return acceptedFiles.map(createFileOutput);
}

export function createFileOutput(file) {
  const fileUrl = URL.createObjectURL(file);
  return { file, fileUrl };
}
